import { Injectable } from '@angular/core';
import { ApiService } from '@core/http';
import { Chart } from 'chart.js';
import { forkJoin, from, map, Observable, switchMap } from 'rxjs';
import { getChart } from 'src/app/core/helpers/chart-plugin-wrapper';
import { getSvg } from 'src/app/core/helpers/get-svg';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { getDecimalPlacesValue } from '../../../custom-fields/custom-field-numbers/utils/getDecimalPlacesValue';
import { TCustomField } from '../../../custom-fields/custom-fields.model';
import { CustomFieldsService } from '../../../custom-fields/custom-fields.service';
import { TDashlet } from '../../../preferences/preferences.model';
import { getCustomFieldIcon } from '../../../site/site-options/utils/get-field-icon';
import { getEmbeddedFontFaceCss } from '../../../white-label/white-label-utils/get-embedded-font-face-css';
import { DashboardChartService } from '../../dashboard-chart/dashboard-chart.service';
import { TExportDashlet } from '../../dashboard-export-modal/dashboard-export-dashlet.model';
import { DashboardService } from '../../dashboard-service/dashboard.service';
import { TDashletValueBreakdownResponseData } from '../../dashboard.consts';
import { TDashletGraph, TDashletGraphDataset } from '../../dashlet-graph.model';
import { getFieldUnit } from './create-value-breakdown-graph/get-field-unit';
import { createNameElement, getFieldNamePlugin } from './field-name-plugin';
import { legendTooltipPlugin } from './legend-tooltip-plugin';
import { getNoDataPlugin } from './no-data-plugin';
import { getTotalCostPlugin } from './total-cost-plugin';
import { generateValueBreakdownDoughnutGraphOptions } from './value-breakdown-doughnut-graph-options';

@Injectable({
  providedIn: 'root',
})
export class CreateValueBreakdownGraphService {
  private chartPlugin = getChart();

  constructor(
    private dashboardChartService: DashboardChartService,
    private dashboardService: DashboardService,
    private customFieldsService: CustomFieldsService,
    private apiService: ApiService,
  ) {}

  createValueBreakdownGraph(
    response: TDashletValueBreakdownResponseData,
    selectedRange: TDashlet['selectedRange'],
    isExport: boolean,
    isMobile: boolean = false,
  ): Observable<TDashletGraph> {
    return new Observable<TDashletGraph>((observer) => {
      const numericalCustomField = this.customFieldsService.getWorkspaceCustomField(
        selectedRange.workspaceId,
        selectedRange.numericalCustomFieldId,
      );
      const listCustomField = this.customFieldsService.getWorkspaceCustomField(
        selectedRange.workspaceId,
        selectedRange.listCustomFieldId,
      );
      const listIcon = getCustomFieldIcon(listCustomField.id);
      const numericalIcon = getCustomFieldIcon(numericalCustomField.id);
      const arrowsIcon = EIconPath.ICON_COMPARE_ARROWS;

      forkJoin([
        getSvg(this.apiService, listIcon),
        getSvg(this.apiService, numericalIcon),
        getSvg(this.apiService, arrowsIcon),
        from(getEmbeddedFontFaceCss()),
      ]).subscribe(([listSvg, numericalSvg, arrowsSvg, fontFaceCss]) => {
        const unit = getFieldUnit(numericalCustomField);
        const decimalPlaces = this.getFieldDecimalPlaces(numericalCustomField);

        const datasets: TDashletGraphDataset[] = [
          {
            data: Object.values(response.data),
            backgroundColor: [
              '#9F5FF0',
              '#00B0F0',
              '#083796',
              '#17C5D9',
              '#04D9B2',
              '#F0C419',
              '#F39C12',
              '#E91E63',
              '#3A54E4',
              '#D500F9',
            ],
          },
        ];

        const data: {
          labels: string[];
          datasets: TDashletGraphDataset[];
        } = {
          labels: this.dashboardChartService.addValueBreakdownGraphLabels(response),
          datasets,
        };

        const nameUrl = createNameElement(
          listCustomField.label,
          numericalCustomField.label,
          isExport,
          listSvg,
          numericalSvg,
          arrowsSvg,
          fontFaceCss,
        );

        createImage(nameUrl, isExport).then((nameImage) => {
          observer.next({
            type: 'doughnut',
            data: data,
            plugins: [
              getNoDataPlugin(isExport),
              getTotalCostPlugin(
                response,
                numericalCustomField.currencyCode,
                unit,
                isExport,
                decimalPlaces,
              ),
              getFieldNamePlugin(nameImage.image as any, nameImage.width, isExport),
              legendTooltipPlugin(isExport),
            ],
            options: generateValueBreakdownDoughnutGraphOptions(
              isMobile,
              isExport,
              unit,
              decimalPlaces,
              response,
              numericalCustomField.currencyCode,
            ),
          });

          observer.complete();
        });
      });
    });
  }

  fetchExportValueBreakdownGraph(
    dashlet: TExportDashlet,
    graph: TDashletGraph, // Modifying input param instead of returning a new instance to maintain legacy structure, needs refactoring whole export feature
    canvas: HTMLCanvasElement,
    assetId: string,
  ): Observable<{
    chart: Chart;
    totalPoints: number;
  }> {
    return this.dashboardService
      .fetchValueBreakdown({
        workspaceId: dashlet.selectedRange.workspaceId,
        listCustomFieldId: dashlet.selectedRange.listCustomFieldId,
        numericalCustomFieldId: dashlet.selectedRange.numericalCustomFieldId,
        assetId,
      })
      .pipe(
        switchMap((response) =>
          this.createValueBreakdownGraph(response, dashlet.selectedRange, true, false),
        ),
        map((graphResponse) => {
          const context = canvas.getContext('2d');
          graph = graphResponse;
          graph.options = { ...graph.options, animation: false, responsive: true };
          const chart = new this.chartPlugin(context, graph);

          return {
            chart,
            totalPoints: 0,
          };
        }),
      );
  }

  private getFieldDecimalPlaces(customField: TCustomField): number {
    return getDecimalPlacesValue(customField.decimalPlaces);
  }
}

function createImage(
  svg: string,
  isExport: boolean,
): Promise<{
  image: HTMLImageElement;
  width: number;
}> {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    container.innerHTML = `<img src="${svg}" style="width: fit-content; height: ${isExport ? 60 : 14}px;" />`;
    const img = container.querySelector('img') as HTMLImageElement;

    img.onload = (): void => {
      // Decode and parse
      const svgText = decodeURIComponent(
        svg.replace(/^data:image\/svg\+xml(;charset=utf-8)?,/, ''),
      );
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgEl = svgDoc.documentElement;

      document.body.appendChild(svgEl);

      requestAnimationFrame(() => {
        let width = 0;
        const elements = svgEl.querySelectorAll('.calculate-width-image');

        elements.forEach((el, i) => {
          width += el.getBoundingClientRect().width;
        });

        resolve({
          image: img,
          width: width || 0,
        });
      });

      setTimeout(() => {
        svgEl.remove();
      }, 1000);
    };
  });
}
